import { useEffect, useLayoutEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { ActionButton } from "../../components/Buttons";
import { auth } from "../../configs/firebaseConfig";
import { useAuth } from "../../contexts/AuthContext";
import {
  getPreferredAuthUser,
  isSameAuthUser,
  waitForSettledAuthUser,
} from "../../utils/auth/authState";
import {
  GOOGLE_AUTH_OUTCOMES,
  signInWithGoogle,
} from "../../utils/auth/googleAuth";
import {
  clearAccountMenuIntent,
  saveAccountMenuIntent,
} from "../../utils/auth/accountSessionRecovery";
import {
  getCardGenerationWithCards,
  getOwnedCards,
} from "../../utils/firestore";
import { getCardLanguagePresentation } from "../../utils/cards/languageAdaptiveCard";
import AccountLinkingSheet from "./components/AccountLinkingSheet";
import BackgroundLights from "./components/BackgroundLights";
import EmpathyPageHeader from "./components/EmpathyPageHeader";
import RefinedCardPreview from "./components/RefinedCardPreview";
import ShareCardPreviewView from "./components/ShareCardPreviewView";
import usePreparedCardShare from "./usePreparedCardShare";

const REFINE_DARK_BACKGROUND = "#050604";
const YOUR_CARDS_PATH = "/your-cards";
const PARTNER_LABELS = {
  mcaad: "MCAAD",
};
const WALL_LABELS = {
  "mcaad-caregiving-popup": "MCAAD Living Wall",
};
const TITLE_CASE_OVERRIDES = {
  mcaad: "MCAAD",
};

async function getInitializedCurrentUser(
  contextCurrentUser,
  expectedOwnerUid,
  shouldWaitForAccountUser = false
) {
  return waitForSettledAuthUser({
    contextUser: contextCurrentUser,
    expectedUid: expectedOwnerUid,
    requirePermanentUser: shouldWaitForAccountUser,
  });
}

function getCleanString(value) {
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

function getSchemaV3Cards(cards) {
  return Array.isArray(cards)
    ? cards.filter(
        (card) => card?.schemaVersion === 3 && card.archivedAt == null
      )
    : [];
}

function getSavedGenerationIdFromLocation(location) {
  return getCleanString(location?.state?.savedGenerationId);
}

function getExpectedOwnerUidFromLocation(location) {
  return getCleanString(location?.state?.expectedOwnerUid);
}

async function getSavedGenerationFallback(savedGenerationId, ownerUid) {
  if (!savedGenerationId || !ownerUid) {
    return null;
  }

  const savedGeneration = await getCardGenerationWithCards(savedGenerationId);

  if (savedGeneration?.generation?.ownerUid !== ownerUid) {
    return null;
  }

  return getSchemaV3Cards(savedGeneration.cards).map((card) => ({
    ...card,
    generationId: card.generationId || savedGeneration.generation.id,
    partnerId: card.partnerId || savedGeneration.generation.partnerId,
    referralSource:
      card.referralSource || savedGeneration.generation.referralSource,
    createdAt: card.createdAt || savedGeneration.generation.createdAt,
    updatedAt: card.updatedAt || savedGeneration.generation.updatedAt,
  }));
}

function getTimestampMillis(timestamp) {
  if (typeof timestamp?.toMillis === "function") {
    return timestamp.toMillis();
  }

  if (timestamp instanceof Date) {
    return timestamp.getTime();
  }

  if (typeof timestamp === "number") {
    return timestamp;
  }

  return 0;
}

function formatCardDate(card) {
  const timestamp =
    getTimestampMillis(card?.createdAt) ||
    getTimestampMillis(card?.updatedAt);

  if (!timestamp) {
    return "Recently created";
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(timestamp));
}

function getCardText(card) {
  const text =
    card?.questionRefined || card?.question || card?.refinedQuestion || "";

  return typeof text === "string" ? text.trim() : "";
}

function getReadableIdLabel(value) {
  const cleanValue = getCleanString(value);

  if (!cleanValue) {
    return "";
  }

  return cleanValue
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => {
      const lowerWord = word.toLowerCase();
      return (
        TITLE_CASE_OVERRIDES[lowerWord] ||
        `${lowerWord.charAt(0).toUpperCase()}${lowerWord.slice(1)}`
      );
    })
    .join(" ");
}

function getPartnerLabel(partnerId) {
  const cleanPartnerId = getCleanString(partnerId);

  if (!cleanPartnerId) {
    return "";
  }

  return PARTNER_LABELS[cleanPartnerId] || getReadableIdLabel(cleanPartnerId);
}

function getWallLabel(wallId) {
  const cleanWallId = getCleanString(wallId);

  if (!cleanWallId) {
    return "";
  }

  return WALL_LABELS[cleanWallId] || getReadableIdLabel(cleanWallId);
}

function getCardContextLabel(card) {
  const wallIds = [
    ...(Array.isArray(card?.wallIds) ? card.wallIds : []),
    card?.wallId,
  ];
  const wallLabels = Array.from(
    new Set(wallIds.map(getWallLabel).filter(Boolean))
  );

  if (wallLabels.length > 0) {
    return wallLabels.join(" · ");
  }

  return getPartnerLabel(card?.partnerId);
}

function getOpenCardPath(card) {
  const cardId = getCleanString(card?.id);
  const generationId = getCleanString(card?.generationId);

  if (generationId) {
    const cardQuery = cardId ? `?cardId=${encodeURIComponent(cardId)}` : "";

    return `/result/${encodeURIComponent(generationId)}${cardQuery}`;
  }

  return cardId ? `/card/${encodeURIComponent(cardId)}` : "";
}

function YourCardsRouteFrame({ children }) {
  return (
    <div className="relative min-h-screen min-h-[100dvh] overflow-hidden bg-[#050604] font-sans text-white">
      <Helmet>
        <meta name="theme-color" content={REFINE_DARK_BACKGROUND} />
        <meta name="apple-mobile-web-app-status-bar-style" content="black" />
      </Helmet>

      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-0 mx-auto h-[70vh] max-w-3xl overflow-visible opacity-80">
        <div className="relative h-full">
          <BackgroundLights showFieldAura={false} />
        </div>
      </div>

      <main className="relative z-10 mx-auto flex min-h-[100dvh] max-w-5xl flex-col overflow-y-auto px-5 pb-28 pt-8 sm:px-8 sm:pt-12">
        {children}
      </main>
    </div>
  );
}

function YourCardsState({ title, message, children }) {
  return (
    <section className="mx-auto flex flex-1 w-full max-w-lg flex-col items-center justify-center py-12 text-center">
      <h1 className="font-serif text-3xl font-medium leading-[0.95] text-white sm:text-5xl">
        {title}
      </h1>
      {message ? (
        <p className="mt-5 text-sm font-semibold leading-relaxed text-white/62">
          {message}
        </p>
      ) : null}
      {children ? <div className="mt-7 w-full max-w-sm">{children}</div> : null}
    </section>
  );
}

function YourCardsCreateFab() {
  return (
    <div className="fixed bottom-5 right-5 z-40 sm:bottom-6 sm:right-6">
      <ActionButton
        to="/refine"
        className="shadow-[0_18px_48px_rgba(0,0,0,0.34)]"
        icon="plus"
        variant="wall"
      >
        Create Card
      </ActionButton>
    </div>
  );
}

function YourCardListItem({ card, onShare }) {
  const presentation = getCardLanguagePresentation(card);
  const preview = presentation.question || getCardText(card);
  const translation = presentation.translation;
  const contextLabel = getCardContextLabel(card);
  const openPath = getOpenCardPath(card);
  const savedDate = formatCardDate(card);
  const accessibleLabel = `Open saved card from ${savedDate}${
    preview ? `: ${preview}` : ""
  }`;
  const previewContent = (
    <>
      <RefinedCardPreview
        result={{
          refinedQuestion:
            preview || "Full card details are available in the result.",
          primaryLanguage: presentation.primaryLanguage,
          primaryLanguageCode: presentation.primaryLanguageCode,
          translation,
          translations: presentation.translations,
        }}
        size="compact"
        showAmbientEffects={false}
        showSparkles={false}
      />

      <div className="mt-3 text-center">
        <p className="text-[11px] font-black uppercase text-[#f4d94e]/76">
          {savedDate}
        </p>
        {contextLabel ? (
          <p className="mt-1 text-xs font-bold leading-snug text-white/54">
            {contextLabel}
          </p>
        ) : null}
      </div>
    </>
  );

  return (
    <li className="flex flex-col items-center justify-center">
      {openPath ? (
        <Link
          to={openPath}
          aria-label={accessibleLabel}
          className="group block rounded-[1.25rem] p-2 outline-none transition duration-200 hover:-translate-y-1 focus-visible:ring-4 focus-visible:ring-[#f4d94e]/45"
        >
          {previewContent}
        </Link>
      ) : (
        <div className="rounded-[1.25rem] p-2">{previewContent}</div>
      )}
      {openPath && card?.id ? (
        <button
          type="button"
          onClick={() => onShare(card)}
          className="mt-1 rounded-full border border-white/18 px-4 py-2 text-xs font-black uppercase tracking-[0.08em] text-white/76 transition hover:border-[#f4d94e]/65 hover:text-[#fff6b8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f4d94e]"
        >
          Share Card
        </button>
      ) : null}
    </li>
  );
}

function YourCardsView() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [authMessage, setAuthMessage] = useState("");
  const [items, setItems] = useState([]);
  const [isAccountLinkingOpen, setIsAccountLinkingOpen] = useState(false);
  const [sessionIntent, setSessionIntent] = useState(null);
  const [shareCard, setShareCard] = useState(null);
  const {
    closeSharePreview,
    copyShareLink,
    openShareSheet,
    prepareShare,
    shareError,
    sharePreview,
    sharing,
    updateSharePreviewMessage,
  } = usePreparedCardShare({ source: "Your Cards" });
  const showSavedFeedback = searchParams.get("saved") === "1";
  const savedGenerationId = getSavedGenerationIdFromLocation(location);
  const expectedOwnerUid = getExpectedOwnerUidFromLocation(location);
  const activeUser = getPreferredAuthUser(
    auth.currentUser,
    currentUser,
    expectedOwnerUid
  );
  const isSignedInAccount = Boolean(activeUser && !activeUser.isAnonymous);
  const isAnonymousUser = activeUser?.isAnonymous === true;

  useLayoutEffect(() => {
    const rootElement = document.getElementById("root");
    const previousHtmlBackground = document.documentElement.style.backgroundColor;
    const previousBodyBackground = document.body.style.backgroundColor;
    const previousRootBackground = rootElement?.style.backgroundColor || "";

    document.documentElement.style.backgroundColor = REFINE_DARK_BACKGROUND;
    document.body.style.backgroundColor = REFINE_DARK_BACKGROUND;

    if (rootElement) {
      rootElement.style.backgroundColor = REFINE_DARK_BACKGROUND;
    }

    return () => {
      document.documentElement.style.backgroundColor = previousHtmlBackground;
      document.body.style.backgroundColor = previousBodyBackground;

      if (rootElement) {
        rootElement.style.backgroundColor = previousRootBackground;
      }
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadCards() {
      setLoading(true);
      setLoadError("");

      const initializedCurrentUser = await getInitializedCurrentUser(
        currentUser,
        expectedOwnerUid,
        showSavedFeedback || Boolean(expectedOwnerUid)
      );

      if (!isMounted) return;

      if (!initializedCurrentUser || initializedCurrentUser.isAnonymous) {
        setItems([]);
        setLoading(false);
        return;
      }

      try {
        const ownedCards = await getOwnedCards(initializedCurrentUser.uid);

        if (!isMounted) return;

        const latestUser = getPreferredAuthUser(
          auth.currentUser,
          currentUser,
          expectedOwnerUid
        );

        if (!isSameAuthUser(latestUser, initializedCurrentUser)) {
          setItems([]);
          setLoading(false);
          return;
        }

        setItems(ownedCards);
      } catch (error) {
        if (!isMounted) return;

        console.error("Unable to load your cards", error);

        if (showSavedFeedback && savedGenerationId) {
          try {
            const savedItem = await getSavedGenerationFallback(
              savedGenerationId,
              initializedCurrentUser.uid
            );

            if (!isMounted) return;

            if (Array.isArray(savedItem) && savedItem.length > 0) {
              setItems(savedItem);
              setLoadError("");
              return;
            }
          } catch (fallbackError) {
            if (!isMounted) return;

            console.error("Unable to load saved card fallback", fallbackError);
          }
        }

        setItems([]);
        setLoadError("We couldn't load your cards just now.");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadCards();

    return () => {
      isMounted = false;
    };
  }, [currentUser, expectedOwnerUid, savedGenerationId, showSavedFeedback]);

  const prepareCardShare = async (card) => {
    const preview = getCardText(card);
    setShareCard(card);
    await prepareShare({
      cardId: card.id,
      question: preview,
    });
  };

  const handleSignedOutGoogleSignIn = async () => {
    setAuthMessage("");
    const result = await signInWithGoogle();

    if (result.status === GOOGLE_AUTH_OUTCOMES.SUCCESS) {
      navigate(YOUR_CARDS_PATH, {
        replace: true,
        state: { expectedOwnerUid: result.uid },
      });
      return;
    }

    if (result.status === GOOGLE_AUTH_OUTCOMES.CANCELLED) {
      return;
    }

    setAuthMessage("We couldn't sign you in just now. Please try again.");
  };

  const handleAnonymousGoogleLinking = () => {
    const savedIntent = saveAccountMenuIntent({
      sourceUid: activeUser?.uid,
      returnTo: `${location.pathname}${location.search}`,
    });

    if (!savedIntent) {
      setAuthMessage("We couldn't save this session for recovery. Please try again.");
      return;
    }

    setSessionIntent(savedIntent);
    setAuthMessage("");
    setIsAccountLinkingOpen(true);
  };

  const handleDismissAccountLinking = () => {
    clearAccountMenuIntent();
    setIsAccountLinkingOpen(false);
  };

  const continueAfterAccountLinking = async ({ authResult } = {}) => {
    clearAccountMenuIntent();
    setIsAccountLinkingOpen(false);
    navigate(YOUR_CARDS_PATH, {
      replace: true,
      state: {
        expectedOwnerUid:
          typeof authResult?.uid === "string" ? authResult.uid : "",
      },
    });
  };

  if (loading) {
    return (
      <YourCardsRouteFrame>
        <EmpathyPageHeader
          title="Your Cards"
          authUser={activeUser}
          signOutRedirectTo="/refine"
        />
        <YourCardsState
          title="Opening your cards..."
          message="Loading your saved cards."
        />
      </YourCardsRouteFrame>
    );
  }

  if (!isSignedInAccount) {
    return (
      <YourCardsRouteFrame>
        <EmpathyPageHeader
          title="Your Cards"
          authUser={activeUser}
          signOutRedirectTo="/refine"
        />
        <YourCardsState
          title="Sign in to see your cards."
          message="Connect Google to see the cards saved with your LettuceTalk account."
        >
          <ActionButton
            type="button"
            onClick={
              isAnonymousUser
                ? handleAnonymousGoogleLinking
                : handleSignedOutGoogleSignIn
            }
            fullWidth
            icon="sparkles"
            variant="wall"
          >
            Continue with Google
          </ActionButton>
          <ActionButton
            to="/refine"
            className="mt-3"
            fullWidth
            icon="sparkles"
            variant="darkSecondary"
          >
            Create Card
          </ActionButton>
          {authMessage ? (
            <p role="status" className="mt-4 text-sm font-bold text-[#fff6b8]">
              {authMessage}
            </p>
          ) : null}
        </YourCardsState>
        <AccountLinkingSheet
          isOpen={isAccountLinkingOpen}
          sessionSave
          sessionIntent={sessionIntent}
          onDismiss={handleDismissAccountLinking}
          onLinked={continueAfterAccountLinking}
        />
      </YourCardsRouteFrame>
    );
  }

  return (
    <YourCardsRouteFrame>
      <EmpathyPageHeader
        title="Your Cards"
        authUser={activeUser}
        signOutRedirectTo="/refine"
      />

      <section className="mx-auto w-full max-w-3xl py-8 sm:py-10">
        {authMessage ? (
          <p role="status" className="text-sm font-bold text-[#fff6b8]">
            {authMessage}
          </p>
        ) : null}

        {loadError ? (
          <div className="rounded-[1.35rem] border border-white/12 bg-white/[0.06] p-5 text-center">
            <h2 className="font-serif text-2xl font-medium text-white">
              We couldn't load your cards.
            </h2>
            <p className="mt-3 text-sm font-semibold leading-6 text-white/62">
              {loadError || "Please try again in a moment."}
            </p>
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-[1.35rem] border border-white/12 bg-white/[0.06] p-5 text-center">
            <h2 className="font-serif text-2xl font-medium text-white">
              No cards here yet.
            </h2>
            <p className="mt-3 text-sm font-semibold leading-6 text-white/62">
              Create a card and it will appear here after it is saved to your account.
            </p>
          </div>
        ) : (
          <ul className="grid gap-x-5 gap-y-8 sm:grid-cols-2">
            {items.map((card) => (
              <YourCardListItem
                key={card.id}
                card={card}
                onShare={prepareCardShare}
              />
            ))}
          </ul>
        )}
      </section>

      <YourCardsCreateFab />
      {sharePreview ? (
        <ShareCardPreviewView
          selectedIntroId={sharePreview.selectedIntroId}
          isPreparing={sharePreview.isPreparing || sharing}
          preparationFailed={sharePreview.preparationFailed}
          shareError={shareError}
          onMessageChange={updateSharePreviewMessage}
          onOpenShareSheet={openShareSheet}
          onPrepareShareLink={() =>
            prepareCardShare(shareCard || {})
          }
          onCopyLink={copyShareLink}
          onClose={() => {
            closeSharePreview();
            setShareCard(null);
          }}
        />
      ) : null}
    </YourCardsRouteFrame>
  );
}

export default YourCardsView;
